import { erpApi } from '@/shared/axios/erp-api';
import { getUrlParamsFetch } from '@/shared/utils/get-url-params';
import { handleAxiosError } from '@/shared/utils/general/axios.utils';
import { urlBackend } from '@/shared/constants/networking.constants';
import { useUiStore } from '@/store/ui/ui.store';
import { useGlobalProgressBarStore } from "@/store/ui/progress-bar.store";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IDataIngresoInventario, IDataIngresoInventarioParams, IIngresoInventario, IIngresoInventarioById, IngresoInventarioForm } from '@/shared/interfaces/erp/administration/transferencia-inventario.interface';
import { DataSerieReingreso } from '@/erp/contabilidad/reingreso-serie';

const { post, get } = erpApi();

/**
 *
 * Crear ingreso inventariable
 *
 * Error 400 serie ya existe en la ubicacion
*/
export const useCreateIngresoProducto = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setTask = useGlobalProgressBarStore.getState().setTask;

  return useMutation({
    mutationFn: postIngresarProducto,
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['ingreso-inventario'] });

      console.log('🔥 Respuesta ingreso inventario:', data);
      console.log('🔥 data.code:', data.code, 'data.data?.task_id:', data.data?.task_id);

      // Si es respuesta 202 (procesamiento asíncrono), iniciar tracking de progreso
      if (data.code === 202 && data.data?.task_id) {
        console.log('🚀 Iniciando progress bar con:', { codigo: data.data.codigo, task_id: data.data.task_id });
        setTask({
          codigo: data.data.codigo,
          task_id: data.data.task_id,
          startTime: Date.now(),
        });
        setAlert(true, data.message, 'info', 5000);
        return
      } else {
        setAlert(true, data.message, data.status);
      }
    },
    onError: (error) => {
      handleAxiosError(error)
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });

};

export const postIngresarProducto = async (data: IngresoInventarioForm) => {
  const resp = await post<IDataIngresoInventario>(`${urlBackend}api/v1/ingreso-inventario/`, data, true);
  return resp;
};

/**
 * 
 * Obtener ingresos de inventariable
 * 
*/

interface IngresoInventariableParams {
  enabled: boolean;
  params?: IDataIngresoInventarioParams;
}
export const useFetchGetIngresarProducto = ({ enabled = false, params }: IngresoInventariableParams) => {

  return useQuery({
    queryKey: ['ingreso-inventario', ...Object.values(params || {})],
    queryFn: async () => {
      return await getIngresarProducto(params)
    },
    enabled: enabled
  })
}
export const getIngresarProducto = async (params?: IDataIngresoInventarioParams) => {
  const queryParams = getUrlParamsFetch(params || {});
  const resp = await get<IIngresoInventario>(`${urlBackend}api/v1/ingreso-inventario/?${queryParams}`, true);
  return resp;
};

export const useGetIngresarProductoById = (id: string) => {
  return useQuery({
    queryKey: ['ingreso-inventario', id],
    queryFn: async () => { return getIngresarProductoById(id) },
    enabled: !!id
  })

}
export const getIngresarProductoById = async (id: string) => {
  return await get<IIngresoInventarioById>(`${urlBackend}api/v1/ingreso-inventario/${id}/`, true)
}

export const postRegenerarPdfIngreso = async (id: string) => {
  return await post<{ codigo: string, url_pdf: string }>(`${urlBackend}api/v1/ingreso-inventario/${id}/regenerar-pdf/`, {}, true)
}
/**
 * 
 * Generar series
 * 
*/
type IGenerarSeriesParams = {
  producto: string;
  prefijo: string;
  cantidad: number;
  inicio?: number;
}

export const useFetchGenerarSeries = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: postGenerarSeries,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['generar-series'] });
      setAlert(true, data.message, data.status);
    },
    onError: error => {
      handleAxiosError(error)
    }
  });
};

export const postGenerarSeries = async (params: IGenerarSeriesParams) => {
  const resp = await post<string[]>(`${urlBackend}api/v1/generar-series/`, params, true);
  return resp;
}

/**
 *
 * Obtener estado de procesamiento de ingreso inventario (para polling)
 *
*/
export interface IEstadoIngresoInventario {
  estado: 'PENDIENTE' | 'PROCESANDO' | 'COMPLETADO' | 'ERROR';
  progreso: number;
  total: number;
  porcentaje: number;
  descripcion: string;
  task_id?: string;
  error?: string;
}

export const getEstadoIngresoInventario = async (codigo: string): Promise<IEstadoIngresoInventario> => {
  const resp = await get<IEstadoIngresoInventario>(`${urlBackend}api/v1/ingreso-inventario/${codigo}/estado/`, true);
  return resp.data;
}

/**
 *
 * Validar series duplicadas antes de crear ingreso
 *
*/
export interface IValidarSeriesDuplicadasParams {
  producto: string;
  series: string[];
}

export interface IValidarSeriesDuplicadasResponse {
  tiene_duplicados: boolean;
  series_duplicadas: string[];
  total_duplicadas: number;
  total_validadas: number;
  tipo_error: 'DUPLICADO'|'REINGRESO';
  series_reingreso: DataSerieReingreso[]
}

export const postValidarSeriesDuplicadas = async (params: IValidarSeriesDuplicadasParams): Promise<IValidarSeriesDuplicadasResponse> => {
  const resp = await post<IValidarSeriesDuplicadasResponse>(`${urlBackend}api/v1/ingreso-inventario/validar-series/`, params, true);
  return resp.data;
}

/**
 *
 * Validar series en ubicación de origen (para traslado de inventario)
 *
 * Este endpoint es específico para el módulo de Traslado de Inventario,
 * donde se necesita verificar que las series a trasladar existen en la
 * bodega/ubicación de origen (a diferencia del ingreso que valida duplicados globales).
 *
*/
export interface IValidarSeriesEnOrigenParams {
  ubicacion_origen: string;
  producto: string;
  series: string[];
}

export interface IValidarSeriesEnOrigenResponse {
  todas_disponibles: boolean;
  series_disponibles: string[];
  series_no_encontradas: string[];
  total_disponibles: number;
  total_no_encontradas: number;
  total_validadas: number;
}

export const postValidarSeriesEnOrigen = async (params: IValidarSeriesEnOrigenParams): Promise<IValidarSeriesEnOrigenResponse> => {
  const resp = await post<IValidarSeriesEnOrigenResponse>(`${urlBackend}api/v1/traslado-inventario/validar-series-origen/`, params, true);
  return resp.data;
}

export interface IReingresoSeries {
  tipo_error: 'DUPLICADO'|'REINGRESO';
  series_reingreso: DataSerieReingreso[];
  openModal: boolean
  referencia_producto: string
  nombre_producto: string
}
